import {TUrlImportItem} from "mapx-components/BulkImport/types";

export const STATUS_COLOR_MAP: {[key: string]: string} = {
	success: "#0C5850FF",
	imported: "#0C5850FF",
	completed: "#0C5850FF",
	"already exists": "#0C5850FF",
	"in progress": "#444444",
	created: "#444444",
	scraped: "#444444",
	"import error": "#F5A300",
	error: "#F5A300",
	"unknown error": "#F5A300",
	"bad url": "#F5A300",
	"not found": "#F5A300",
	"scraping error": "#F5A300",
};

export const STATUS_MESSAGES: {[key: string]: string} = {
	created: "Processing...",
	scraped: "Scraped...",
	imported: "Success",
	completed: "Success",
	"already exists": "Success",
	"in progress": "Processing...",
	"import error": "Error",
	"scraping error": "Error",
	"bad url": "Not a valid URL",
	"not found": "Not a valid URL",
};

// Statuses that indicate the URL is still being processed (not finished, not an error)
export const IN_PROGRESS_STATUSES = [
	"waiting",
	"importing",
	"scraping",
	"in progress",
	"created",
	"scraped",
];

/**
 * Check if a status indicates the URL is still being processed
 */
export const isInProgressStatus = (status: string): boolean =>
	IN_PROGRESS_STATUSES.includes(status?.toLowerCase());

export const CalculateStatusTypeCounts = (linkedInUrl?: TUrlImportItem[]) => {
	const statusCounts = {
		success: 0,
		error: 0,
		badURL: 0,
		notFound: 0,
		finished: 0,
		inProgress: 0,
	};

	linkedInUrl?.forEach(({status}) => {
		const lowerStatus = status?.toLowerCase();
		if (["success", "imported", "completed", "already exists"].includes(lowerStatus)) {
			statusCounts.success++;
			statusCounts.finished++;
		} else if (["bad url", "not found", "scraping error"].includes(lowerStatus)) {
			lowerStatus === "bad url" ? statusCounts.badURL++ : statusCounts.notFound++;
			lowerStatus === "scraping error" && statusCounts.error++;
			statusCounts.finished++;
		} else if (IN_PROGRESS_STATUSES.includes(lowerStatus)) {
			// In-progress statuses are NOT errors and NOT finished
			statusCounts.inProgress++;
		} else {
			// Actual error statuses (like "import error", "error", "unknown error")
			statusCounts.error++;
			if (["import error"].includes(lowerStatus)) {
				statusCounts.finished++;
			}
		}
	});

	return statusCounts;
};

/**
 * Formats ETA information for display.
 * Calculates remaining time from eta_expected_at, or falls back to eta_original.
 * Rounds up to the next whole unit (e.g., 3h 1min = ~4 hours).
 * @param etaExpectedAt - ISO timestamp of expected completion
 * @param etaOriginal - Fallback string representation
 * @returns Human-readable ETA string with ~ prefix (e.g., "~4 hours", "~30 minutes", "-")
 */
export const formatETA = (etaExpectedAt?: string | null, etaOriginal?: string | null): string => {
	// If no ETA data available, return dash
	if (!etaExpectedAt && !etaOriginal) {
		return "-";
	}

	// If we have an expected timestamp, try to calculate remaining time
	if (etaExpectedAt) {
		const expectedDate = new Date(etaExpectedAt);
		const now = new Date();
		const diffMs = expectedDate.getTime() - now.getTime();

		// If the expected time is in the future, calculate remaining time
		if (diffMs > 0) {
			// Only show days if >= 24 hours
			if (diffMs >= 1000 * 60 * 60 * 24) {
				const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));

				return diffDays === 1 ? "~1 day" : `~${diffDays} days`;
			} else if (diffMs >= 1000 * 60 * 60) {
				// Only show hours if >= 1 hour
				const diffHours = Math.ceil(diffMs / (1000 * 60 * 60));

				return diffHours === 1 ? "~1 hour" : `~${diffHours} hours`;
			} else if (diffMs >= 1000 * 60) {
				// Only show minutes if >= 1 minute
				const diffMinutes = Math.ceil(diffMs / (1000 * 60));

				return diffMinutes === 1 ? "~1 minute" : `~${diffMinutes} minutes`;
			} else {
				return "~< 1 minute";
			}
		}
	}

	// If eta_expected_at is in the past or invalid, fallback to eta_original
	if (etaOriginal) {
		return etaOriginal;
	}

	return "-";
};
